import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { FormattedNumber } from "./FormattedNumber";

interface ResultOverlayProps {
  isWin: boolean;
  title?: string;
  icon?: string;
  rewards?: {
    gold: number;
    xp: number;
    diamonds?: number;
    insignia?: number;
    arcane_dust?: number;
    shards?: number;
    shard_level?: number;
    legendary_stones?: number;
    dungeon_cleared?: boolean;
  } | null;
  buttonLabel?: string;
  saving?: boolean;
  /** When set, hides the button and auto-calls onContinue after N seconds. */
  autoDismissSeconds?: number;
  onContinue: () => void;
}

/**
 * Rewards are always granted automatically on the backend the moment a
 * battle resolves — this overlay is purely informational/dismissive, so it
 * only ever shows a single continue button (no separate "claim" step).
 */
export function ResultOverlay({
  isWin,
  title,
  icon,
  rewards,
  buttonLabel = "OK",
  saving = false,
  autoDismissSeconds,
  onContinue,
}: ResultOverlayProps) {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(
    autoDismissSeconds != null ? autoDismissSeconds : null,
  );
  const continuedRef = useRef(false);
  const onContinueRef = useRef(onContinue);
  onContinueRef.current = onContinue;

  useEffect(() => {
    if (autoDismissSeconds == null || saving) return;
    setSecondsLeft(autoDismissSeconds);
    continuedRef.current = false;
    const startedAt = Date.now();
    const tick = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      const left = Math.max(0, autoDismissSeconds - elapsed);
      setSecondsLeft(left);
      if (left <= 0 && !continuedRef.current) {
        continuedRef.current = true;
        clearInterval(tick);
        onContinueRef.current();
      }
    }, 200);
    return () => clearInterval(tick);
  }, [autoDismissSeconds, saving]);

  return (
    <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-40 p-6">
      <div
        className={clsx(
          "w-full rounded-2xl p-6 text-center border-2 space-y-4 animate-bounce-in",
          isWin ? "bg-emerald-950/90 border-emerald-500" : "bg-rose-950/90 border-rose-600",
        )}
      >
        <div className="text-6xl">{icon ?? (isWin ? "🏆" : "💀")}</div>
        <div className={clsx("text-3xl font-bold font-game", isWin ? "text-emerald-400" : "text-rose-400")}>
          {title ?? (isWin ? "Victory!" : "Defeated")}
        </div>

        {rewards && (
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm">
            {!!rewards.gold && (
              <span className="text-gold-400">🪙 +<FormattedNumber value={rewards.gold} /></span>
            )}
            {!!rewards.xp && (
              <span className="text-blue-400">⭐ +<FormattedNumber value={rewards.xp} /> XP</span>
            )}
            {!!rewards.diamonds && (
              <span className="text-purple-400">💎 +<FormattedNumber value={rewards.diamonds} /></span>
            )}
            {!!rewards.insignia && (
              <span className="text-amber-400">🎖 +<FormattedNumber value={rewards.insignia} /></span>
            )}
            {!!rewards.arcane_dust && (
              <span className="text-violet-300">✨ +<FormattedNumber value={rewards.arcane_dust} /></span>
            )}
            {!!rewards.shards && (
              <span className="text-cyan-300">
                🔷 +{rewards.shards} shard{rewards.shards > 1 ? "s" : ""}
                {rewards.shard_level ? ` Lv.${rewards.shard_level}` : ""}
              </span>
            )}
            {!!rewards.legendary_stones && (
              <span className="text-orange-300">
                💠 +{rewards.legendary_stones} legendary stone{rewards.legendary_stones > 1 ? "s" : ""}
              </span>
            )}
          </div>
        )}

        {rewards?.dungeon_cleared && (
          <div className="text-sm text-emerald-300 font-semibold">Dungeon cleared!</div>
        )}

        {saving && <div className="text-[11px] text-gray-500 animate-pulse">Saving rewards…</div>}

        {autoDismissSeconds != null && !saving && secondsLeft != null && (
          <p className="text-[11px] text-gray-400 tabular-nums">Closing in {secondsLeft}s</p>
        )}

        {autoDismissSeconds == null && (
          <button
            onClick={onContinue}
            className="w-full py-3 bg-gradient-to-r from-gold-500 to-gold-600 text-black font-bold rounded-xl active:scale-95 transition-transform"
          >
            {buttonLabel}
          </button>
        )}
      </div>
    </div>
  );
}
